'use client'

import { useAuthorization } from '@entities/authentication'
import {
  subscribesTicketEntityQueries,
  SubscribeTicketCard,
  SubscribeTicketLanguageChip,
  SubscribeTicketLessonCountChip,
} from '@entities/subscribes'
import {
  LangType,
  LessonCountPerMonth,
  lessonCountPerMonthSchema,
  LessonTime,
  lessonTimeSchema,
  paymentTypeSchema,
} from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { transformNumberEnumToStringEnum } from '@shared/libs'
import { useSuspenseQuery } from '@tanstack/react-query'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import z from 'zod'

import { Box, Flex, HStack, Typography, VStack } from '@podo-app/design-system-temp'

import { LECTURE_TICKETS_QUERY_PARAMS } from '../../model'

type Props = {
  langType: LangType
  lessonTime: LessonTime
  lessonCountPerMonth: LessonCountPerMonth
}

export const LumpSumLectureTickets = ({ langType, lessonTime, lessonCountPerMonth }: Props) => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const searchParams = useSearchParams()
  const router = useRouter()

  const { data: subscribeTicketsData } = useSuspenseQuery({
    ...subscribesTicketEntityQueries.lumpSumList({
      bearerToken,
      langType,
      lessonTime: lessonTime.toString() as NumberEnumToStringEnum<z.infer<typeof lessonTimeSchema>>,
      lessonCountPerMonth: lessonCountPerMonth.toString() as NumberEnumToStringEnum<
        z.infer<typeof lessonCountPerMonthSchema>
      >,
    }),
  })

  const subscribeTickets = Array.isArray(subscribeTicketsData) ? subscribeTicketsData : []

  return (
    <VStack className="gap-8">
      <VStack className="gap-5">
        <VStack className="gap-2">
          <Typography size="h1" className="text-[28px] font-bold">
            {t('subscribeList.paymentTypeHero.lumpSumTitle')}
          </Typography>
          <Typography size="h4" className="text-[18px]">
            {t('subscribeList.paymentTypeHero.lumpSumLine1')}
            <br />
            {t('subscribeList.paymentTypeHero.lumpSumLine2')}
          </Typography>
        </VStack>

        <HStack className="gap-2.5">
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">{t('subscribeList.paymentTypeHero.lowestPriceTicket')}</Typography>
          </Box>
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">{t('subscribeList.chip.unlimited')}</Typography>
          </Box>
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">{t('subscribeList.paymentTypeHero.specialPrice')}</Typography>
          </Box>
        </HStack>

        <Flex className="relative w-full h-[335px]">
          <Image
            src="/images/lump-sum-package-banner.png"
            alt={t('subscribeList.paymentTypeHero.lumpSumBannerAlt')}
            fill
            sizes="(max-width: 768px) 540px, 100vw"
            className="object-contain"
          />
        </Flex>
      </VStack>
      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.language')}</Typography>

        <SubscribeTicketLanguageChip
          selectedLanguage={langType}
          handleChangeLanguage={(language) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.langType, language)

            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
        />
      </VStack>

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.lessonCount')}</Typography>

        <SubscribeTicketLessonCountChip
          selectedLessonCount={transformNumberEnumToStringEnum(lessonCountPerMonth)}
          handleChangeLessonCount={(selectedLessonCount) => {
            const queryParams = new URLSearchParams(searchParams.toString())

            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.lessonCountPerMonth, selectedLessonCount)
            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
          paymentType={paymentTypeSchema.enum.SUBSCRIBE}
        />
      </VStack>
      {/* NOTE: 레슨 시간 추가 시 활성화 */}
      {/* <VStack className="gap-4">
        <Typography size="h3">레슨 시간을 선택하세요.</Typography>

        <SubscribeTicketLessonTimeChip
          selectedLessonTime={transformNumberEnumToStringEnum(lessonTime)}
          handleChangeLessonTime={(selectedLessonTime) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.lessonTime, selectedLessonTime)

            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
        />
      </VStack> */}
      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.product')}</Typography>

        {subscribeTickets.map((subscribeTicket) => (
          <SubscribeTicketCard
            month={subscribeTicket.lessonMonth}
            key={subscribeTicket.id}
            subscribeTicketId={subscribeTicket.id}
            catalogTicket={subscribeTicket}
            price={subscribeTicket.originPrice}
            descriptions={[
              t('subscribeList.ticketDescriptions.dailyFast'),
              t('subscribeList.ticketDescriptions.materials'),
            ]}
            isBest={subscribeTicket.isBest}
          />
        ))}
      </VStack>
    </VStack>
  )
}
